import React, { useState, useEffect } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import "./JuridentPayment.css";
import JuridentPaymentConfig from "./config";

const JuridentPayment = () => {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(window.location.search);

    // Check if environment mode is passed from Flutter app
    const isTestModeParam = params.get("isTestMode");
    if (isTestModeParam !== null) {
      // Override config with the mode passed from Flutter app
      JuridentPaymentConfig.isTestMode = isTestModeParam === 'true';
      console.log('🔧 [Config Override] Environment mode set from URL parameter:', isTestModeParam);
    }

    const data = {
      userId: params.get("userId"),
      amount: params.get("amount"),
      phoneNo: params.get("phoneNo"),
      category: params.get("category"),
      transactionId: params.get("transactionId"),
      userName: params.get("userName"),
      platform: params.get("platform") || "IOS",
    };

    // Validate required parameters
    if (!data.userId || !data.amount || !data.phoneNo || !data.transactionId) {
      setError("Missing required payment parameters");
      return;
    }

    setPaymentData(data);

    // Debug logging
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📄 [Jurident Payment Page] Payment data received');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('Environment:', JuridentPaymentConfig.environmentName);
    console.log('Test Mode:', JuridentPaymentConfig.isTestMode);
    console.log('Amount (paise):', data.amount);
    console.log('Amount (rupees):', parseInt(data.amount) / 100);
    console.log('Category:', data.category);
    console.log('Transaction ID:', data.transactionId);
    console.log('User ID:', data.userId);
    console.log('Platform:', data.platform);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }, []);

  const initiatePayment = async () => {
    if (!paymentData) return;

    setLoading(true);

    try {
      // Call the PhonePe payment backend API (same as used in course registration)
      // Convert amount from paise to rupees for PhonePe API
      const amountInRupees = parseInt(paymentData.amount) / 100;

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('💳 [Jurident Payment] Initiating PhonePe payment');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('Amount received (paise):', paymentData.amount);
      console.log('Amount sending to PhonePe (rupees):', amountInRupees);
      console.log('Transaction ID:', paymentData.transactionId);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Store payment context in localStorage before redirecting to PhonePe
      // This allows us to identify Jurident payments on the success page
      const paymentContext = {
        source: 'jurident-ios',
        userId: paymentData.userId,
        transactionId: paymentData.transactionId,
        amount: paymentData.amount, // Store original amount in paise
        category: paymentData.category,
        phoneNo: paymentData.phoneNo,
        userName: paymentData.userName,
        platform: paymentData.platform,
        timestamp: Date.now()
      };
      localStorage.setItem('jurident_payment_context', JSON.stringify(paymentContext));
      console.log('💾 [Jurident Payment] Stored payment context in localStorage');

      // Log current configuration
      console.log('🔧 [Config] Using configuration:', JuridentPaymentConfig.environmentName);
      console.log('🔧 [Config] PhonePe URL:', JuridentPaymentConfig.phonePeUrl);

      const response = await axios.post(
        JuridentPaymentConfig.phonePeUrl,
        {
          name: paymentData.userName || "Jurident User",
          amount: amountInRupees,
          number: paymentData.phoneNo,
          MUID: paymentData.userId,
          transactionId: paymentData.transactionId,
          // Add metadata for callback handling
          metadata: {
            source: "jurident-ios",
            userId: paymentData.userId,
            category: paymentData.category,
            platform: paymentData.platform,
          },
        }
      );

      // Redirect to PhonePe payment page
      if (response.status === 200 && response.data.redirectUrl) {
        console.log("Redirecting to PhonePe:", response.data.redirectUrl);
        window.location.href = response.data.redirectUrl;
      } else {
        throw new Error("Failed to get payment redirect URL");
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      setError(error.message || "Failed to initiate payment");
      toast.error("Payment initiation failed. Please try again.");
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="jurident-payment-container">
        <Toaster position="top-center" />
        <div className="error-container">
          <h1>Payment Error</h1>
          <p>{error}</p>
          <div style={{
            background: JuridentPaymentConfig.colors.primaryLight + '20',
            borderLeft: `4px solid ${JuridentPaymentConfig.colors.primary}`,
            padding: "16px",
            borderRadius: "8px",
            marginTop: "20px",
            textAlign: "center"
          }}>
            <p style={{ color: JuridentPaymentConfig.colors.text, margin: 0, fontSize: "14px", fontWeight: "500" }}>
              You can close this browser and go back to the Jurident app
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="jurident-payment-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading payment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jurident-payment-container">
      <Toaster position="top-center" />

      <div className="payment-card">
        <div className="app-header">
          <h1>Jurident</h1>
          <p className="tagline">Payment Gateway</p>
          {/* Environment Badge */}
          {JuridentPaymentConfig.isTestMode && (
            <div style={{
              background: '#ffc107',
              color: '#000',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              marginTop: '10px',
              display: 'inline-block'
            }}>
              🧪 TEST MODE
            </div>
          )}
        </div>

        <div className="payment-details">
          <h2>Payment Summary</h2>

          <div className="detail-row">
            <span className="label">Amount:</span>
            <span className="value">₹{(paymentData.amount / 100).toFixed(2)}</span>
          </div>

          <div className="detail-row">
            <span className="label">Plan:</span>
            <span className="value">{paymentData.category || "Subscription"}</span>
          </div>

          <div className="detail-row">
            <span className="label">Phone:</span>
            <span className="value">{paymentData.phoneNo}</span>
          </div>

          <div className="detail-row">
            <span className="label">Transaction ID:</span>
            <span className="value transaction-id">{paymentData.transactionId}</span>
          </div>
        </div>

        <div className="payment-info">
          <p>You will be redirected to PhonePe to complete your payment securely.</p>
        </div>

        <button
          onClick={initiatePayment}
          disabled={loading}
          className="payment-button"
        >
          {loading ? (
            <>
              <div className="button-spinner"></div>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Proceed to Pay ₹{(paymentData.amount / 100).toFixed(2)}</span>
            </>
          )}
        </button>

        <div className="security-badge">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span>Secured by PhonePe</span>
        </div>
      </div>
    </div>
  );
};

export default JuridentPayment;
